import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { apiError } from "../lib/format";
import { Plus, PencilSimple, Trash, X, Check, Lightning } from "@phosphor-icons/react";

/**
 * Slash-command dropdown for advisor quick replies.
 * Props:
 *   open: boolean
 *   query: string (text after the '/')
 *   onPick(text): insert text into input, close
 *   onClose()
 *   anchorBottom (px from bottom where to show)
 */
export default function QuickReplies({ open, query, onPick, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [managing, setManaging] = useState(null); // null | "new" | id
  const [form, setForm] = useState({ title: "", text: "" });
  const titleRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat/quick-replies");
      setItems(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => {
    if (managing && titleRef.current) titleRef.current.focus();
  }, [managing]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (r) => r.title.toLowerCase().includes(q) || r.text.toLowerCase().includes(q)
    );
  }, [items, query]);

  const saveNew = async () => {
    if (!form.title.trim() || !form.text.trim()) return toast.error("Título y texto requeridos");
    try {
      await api.post("/chat/quick-replies", { title: form.title.trim(), text: form.text.trim() });
      toast.success("Plantilla creada");
      setForm({ title: "", text: "" });
      setManaging(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async (id) => {
    if (!form.title.trim() || !form.text.trim()) return toast.error("Título y texto requeridos");
    try {
      await api.patch(`/chat/quick-replies/${id}`, { title: form.title.trim(), text: form.text.trim() });
      toast.success("Plantilla actualizada");
      setManaging(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    try {
      await api.delete(`/chat/quick-replies/${id}`);
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!open) return null;

  const isEditing = managing && managing !== "new";

  return (
    <div
      data-testid="quick-replies-dropdown"
      className="absolute bottom-full left-0 right-0 mb-2 bg-surface border border-hairline shadow-lg max-h-[320px] flex flex-col z-20"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-hairline bg-bg">
        <div className="flex items-center gap-2 label-mono">
          <Lightning size={14} weight="duotone" />
          Respuestas rápidas
          {query && <span className="normal-case tracking-normal text-inkMuted">· filtrando "{query}"</span>}
        </div>
        <div className="flex gap-1">
          {!managing && (
            <button
              onClick={() => { setForm({ title: "", text: query ? "" : "" }); setManaging("new"); }}
              className="h-7 px-2 border border-hairline text-xs flex items-center gap-1 hover:bg-forest hover:text-bg hover:border-forest"
              data-testid="qr-new-btn"
            >
              <Plus size={12} weight="bold" /> Nueva
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-bg" data-testid="qr-close">
            <X size={14} />
          </button>
        </div>
      </div>

      {managing && (
        <div className="p-3 border-b border-hairline bg-bg space-y-2" data-testid="qr-form">
          <input
            ref={titleRef}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Título"
            className="w-full h-9 px-2 border border-hairline bg-surface text-sm"
            data-testid="qr-form-title"
          />
          <textarea
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="Texto de la respuesta…"
            rows={3}
            className="w-full py-2 px-2 border border-hairline bg-surface text-sm resize-none"
            data-testid="qr-form-text"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setManaging(null); setForm({ title: "", text: "" }); }} className="h-8 px-3 border border-hairline text-xs">
              Cancelar
            </button>
            <button
              onClick={() => (isEditing ? saveEdit(managing) : saveNew())}
              className="h-8 px-3 bg-forest text-bg text-xs font-semibold flex items-center gap-1"
              data-testid="qr-form-save"
            >
              <Check size={12} weight="bold" /> Guardar
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="p-6 text-center text-inkMuted text-xs">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-inkMuted text-xs">
            {items.length === 0 ? "No tienes plantillas. Crea la primera." : "Sin resultados."}
          </div>
        ) : (
          filtered.map((r) => (
            <div
              key={r.id}
              className="group border-b border-hairline px-3 py-2 hover:bg-bg flex items-start justify-between gap-2"
              data-testid={`qr-item-${r.id}`}
            >
              <button
                onClick={() => onPick(r.text)}
                className="flex-1 text-left min-w-0"
                data-testid={`qr-pick-${r.id}`}
              >
                <div className="text-sm font-semibold truncate">{r.title}</div>
                <div className="label-mono mt-0.5 line-clamp-2 normal-case tracking-normal text-inkMuted text-xs whitespace-pre-wrap break-words">{r.text}</div>
              </button>
              <div className="flex-shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => { setForm({ title: r.title, text: r.text }); setManaging(r.id); }}
                  className="p-1 border border-hairline hover:bg-forest hover:text-bg hover:border-forest"
                  title="Editar"
                  data-testid={`qr-edit-${r.id}`}
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
                <button
                  onClick={() => remove(r.id)}
                  className="p-1 border border-danger text-danger hover:bg-danger hover:text-bg"
                  title="Eliminar"
                  data-testid={`qr-delete-${r.id}`}
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
